import { useMemo, useState } from 'react';
import { Plus, Filter, Mail, MessageSquare, Megaphone, Layers, X } from 'lucide-react';
import { useCampaigns, useSaveCampaigns } from '@/hooks/useDashboard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { CampaignCard } from '@/components/CampaignCard';
import { CampaignDetail } from '@/components/CampaignDetail';
import { fmtEuro, cn } from '@/lib/utils';
import type { Campaign, CampaignChannel, CampaignStatus } from '@/lib/types';

const channels: Array<{ value: CampaignChannel | 'all'; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'all', label: 'Tous', icon: Filter },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'ads', label: 'Pub', icon: Megaphone },
  { value: 'mixed', label: 'Multi', icon: Layers },
];

const statuses: Array<{ value: CampaignStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Tous statuts' },
  { value: 'draft', label: 'Brouillon' },
  { value: 'scheduled', label: 'Programmée' },
  { value: 'active', label: 'En cours' },
  { value: 'paused', label: 'En pause' },
  { value: 'completed', label: 'Terminée' },
];

function uid() { return Math.random().toString(36).slice(2, 9); }

export function Campaigns() {
  const { data, isLoading } = useCampaigns();
  const save = useSaveCampaigns();
  const campaigns = data ?? [];

  const [activeId, setActiveId] = useState<string | null>(null);
  const [chanFilter, setChanFilter] = useState<CampaignChannel | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      if (chanFilter !== 'all' && c.channel !== chanFilter) return false;
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (search.trim() && !c.name.toLowerCase().includes(search.toLowerCase()) && !(c.objective ?? '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [campaigns, chanFilter, statusFilter, search]);

  const active = activeId ? campaigns.find((c) => c.id === activeId) : null;

  const stats = useMemo(() => {
    const live = campaigns.filter((c) => c.status === 'active');
    const totalRevenue = campaigns.reduce((s, c) => s + (c.kpi_actuals.revenue_eur ?? 0), 0);
    const totalSpend = campaigns.reduce((s, c) => s + (c.kpi_actuals.spend_eur ?? 0), 0);
    const totalConv = campaigns.reduce((s, c) => s + (c.kpi_actuals.conversions ?? 0), 0);
    const totalAudience = campaigns.reduce((s, c) => s + (c.audience.size ?? 0), 0);
    return { total: campaigns.length, live: live.length, totalRevenue, totalSpend, totalConv, totalAudience };
  }, [campaigns]);

  const createCampaign = (channel: CampaignChannel) => {
    const newCampaign: Campaign = {
      id: uid(),
      name: 'Nouvelle campagne',
      channel,
      status: 'draft',
      start_date: new Date().toISOString().slice(0, 10),
      end_date: new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10),
      audience: { size: 0 },
      promo_code: '',
      promo_value_pct: 0,
      budget_eur: 0,
      kpi_targets: {},
      kpi_actuals: {},
      links: [],
      notes: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    save.mutate([...campaigns, newCampaign]);
    setCreating(false);
    setActiveId(newCampaign.id);
  };

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      {/* KPIs en tête */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <MetaStat label="Campagnes" value={stats.total} sub={`${stats.live} en cours`} />
        <MetaStat label="Audience cumulée" value={stats.totalAudience.toLocaleString('fr-FR')} sub="contacts" />
        <MetaStat label="Revenue généré" value={fmtEuro(stats.totalRevenue)} tone="success" />
        <MetaStat label="Dépense pub" value={fmtEuro(stats.totalSpend)} tone={stats.totalSpend > 0 ? 'warning' : 'muted'} />
        <MetaStat label="Conversions" value={stats.totalConv} sub="cumulées" tone="info" />
      </div>

      {/* Barre filtres + actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="max-w-xs" />

        <div className="flex items-center gap-1 rounded-md border p-1">
          {channels.map((ch) => {
            const Icon = ch.icon;
            const active = chanFilter === ch.value;
            return (
              <button
                key={ch.value}
                onClick={() => setChanFilter(ch.value)}
                className={cn(
                  'inline-flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors',
                  active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                )}
              >
                <Icon className="h-3 w-3" />
                {ch.label}
              </button>
            );
          })}
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as CampaignStatus | 'all')}>
          <SelectTrigger className="h-9 w-40 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {statuses.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button className="ml-auto" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> Nouvelle campagne
        </Button>
      </div>

      {/* Layout list + detail */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,400px)_1fr]">
        <div className="space-y-2">
          {filtered.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              isActive={c.id === activeId}
              onClick={() => setActiveId(c.id === activeId ? null : c.id)}
            />
          ))}
          {filtered.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
                <Filter className="h-6 w-6" />
                Aucune campagne ne correspond aux filtres
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:sticky lg:top-20 lg:self-start">
          {active ? (
            <CampaignDetail campaign={active} onClose={() => setActiveId(null)} />
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
                Sélectionne une campagne pour voir les détails
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog création */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle campagne — choisir le canal</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {(['email', 'sms', 'ads', 'mixed'] as CampaignChannel[]).map((ch) => {
              const c = channels.find((x) => x.value === ch)!;
              const Icon = c.icon;
              return (
                <button
                  key={ch}
                  onClick={() => createCampaign(ch)}
                  className="flex flex-col items-center justify-center gap-2 rounded-lg border p-6 transition-colors hover:bg-accent"
                >
                  <Icon className="h-6 w-6" />
                  <span className="font-semibold">{c.label}</span>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaStat({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'success' | 'warning' | 'info' | 'muted' }) {
  const toneCls = {
    success: 'text-success',
    warning: 'text-warning',
    info: 'text-info',
    muted: 'text-muted-foreground',
  } as const;
  return (
    <Card>
      <CardContent className="space-y-0.5 p-3">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={cn('num text-2xl font-bold', tone && toneCls[tone])}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}
